import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Size pill click delegation (legacy, kept for compat)
    this.addEventListener('click', this.#handleSizePillClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#handleSizePillClick);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Handles click on size pill buttons via event delegation.
   * @param {MouseEvent} event
   */
  #handleSizePillClick = (event) => {
    const pill = event.target.closest('.cart-items__size-pill');
    if (!pill || pill.disabled) return;

    const line = parseInt(pill.dataset.line, 10);
    const variantId = pill.dataset.variantId;
    if (line && variantId) {
      this.onSizeChange(line, variantId);
    }
  };

  /**
   * Enters edit mode for a cart line item.
   * Shows quantity selector, swaps size text for dropdown, shows confirm/cancel buttons.
   * @param {number} line - The line item index (1-based).
   */
  onToggleEditPanel(line) {
    this.#setEditMode(line, true);
  }

  /**
   * Confirms edit mode changes: applies size change and/or quantity change.
   * @param {number} line - The line item index (1-based).
   */
  async onConfirmEdit(line) {
    const sizeSelect = this.refs[`cartEditSizeSelect-${line}`];
    const qtyWrap = this.refs[`cartEditQty-${line}`];
    const qtyInput = qtyWrap instanceof HTMLElement ? qtyWrap.querySelector('input[type="number"]') : null;

    // Determine new variant ID (if size changed)
    let newVariantId = null;
    if (sizeSelect instanceof HTMLSelectElement) {
      const currentVariantId = sizeSelect.dataset.currentVariantId;
      if (currentVariantId && sizeSelect.value && sizeSelect.value !== currentVariantId) {
        newVariantId = sizeSelect.value;
      }
    }

    // Determine new quantity
    let newQuantity = null;
    if (qtyInput instanceof HTMLInputElement) {
      const parsed = parseInt(qtyInput.value, 10);
      if (!Number.isNaN(parsed) && parsed !== parseInt(qtyInput.defaultValue, 10)) {
        newQuantity = parsed;
      }
    }

    if (newVariantId) {
      // Size change triggers a full remove+add which re-renders the cart
      await this.onSizeChange(line, newVariantId);
    } else if (newQuantity !== null) {
      // Only quantity changed
      this.updateQuantity({ line, quantity: newQuantity, action: 'change' });
    } else {
      // No changes — just collapse
      this.#setEditMode(line, false);
    }
  }

  /**
   * Cancels edit mode: reverts dropdown and quantity to original values, collapses UI.
   * @param {number} line - The line item index (1-based).
   */
  onCancelEdit(line) {
    const sizeSelect = this.refs[`cartEditSizeSelect-${line}`];
    const qtyWrap = this.refs[`cartEditQty-${line}`];
    const qtyInput = qtyWrap instanceof HTMLElement ? qtyWrap.querySelector('input[type="number"]') : null;

    // Revert size dropdown to the originally selected option
    if (sizeSelect instanceof HTMLSelectElement) {
      const initialSelected = sizeSelect.querySelector('option[selected]');
      if (initialSelected instanceof HTMLOptionElement) {
        sizeSelect.value = initialSelected.value;
      }
    }

    // Revert quantity input
    if (qtyInput instanceof HTMLInputElement) {
      qtyInput.value = qtyInput.defaultValue;
    }

    this.#setEditMode(line, false);
  }

  /**
   * Shows or hides edit mode UI for a cart line item.
   * @param {number} line
   * @param {boolean} isEditing
   */
  #setEditMode(line, isEditing) {
    const qtyWrap = this.refs[`cartEditQty-${line}`];
    const sizeText = this.refs[`cartEditSizeText-${line}`];
    const sizeSelect = this.refs[`cartEditSizeSelect-${line}`];
    const actions = this.refs[`cartEditActions-${line}`];
    const actionsEdit = this.refs[`cartEditActionsEdit-${line}`];
    const row = this.refs.cartItemRows?.[line - 1];
    const quantityCell = row?.querySelector('.cart-items__quantity');

    const toggle = (el, hide) => {
      if (el instanceof HTMLElement) {
        el.classList.toggle('hidden', hide);
      }
    };

    toggle(qtyWrap, !isEditing);
    toggle(sizeText, isEditing);
    toggle(actions, isEditing);
    toggle(actionsEdit, !isEditing);

    // Move size select to quantity cell when editing, back to variants when canceling
    if (sizeSelect instanceof HTMLElement && quantityCell instanceof HTMLElement) {
      if (isEditing) {
        // Remember original parent for restore
        if (!sizeSelect.dataset.originalParent) {
          sizeSelect.dataset.originalParent = 'variants';
        }
        quantityCell.prepend(sizeSelect);
        sizeSelect.classList.remove('hidden');
      } else {
        // Move back to the sibling of the sizeText span
        if (sizeText instanceof HTMLElement && sizeText.parentNode) {
          sizeText.parentNode.insertBefore(sizeSelect, sizeText.nextSibling);
        }
        sizeSelect.classList.add('hidden');
      }
    }

    if (row instanceof HTMLElement) {
      row.classList.toggle('cart-items__table-row--editing', isEditing);
    }
  }

  /**
   * Handles size change in the cart.
   * Removes old variant and adds new one with same quantity.
   * @param {number} line - The line item index (1-based).
   * @param {number} variantId - The new variant ID.
   */
  async onSizeChange(line, variantId) {
    const sizeSelect = this.refs[`cartEditSizeSelect-${line}`];
    const qtyWrap = this.refs[`cartEditQty-${line}`];

    // Get current variant ID from the select's data attribute
    let currentVariantId = null;
    if (sizeSelect instanceof HTMLElement) {
      currentVariantId = sizeSelect.dataset.currentVariantId;
    }

    if (currentVariantId && String(variantId) === String(currentVariantId)) return;

    // Determine quantity: prefer current input value if edit panel is visible
    let quantity = 1;
    if (qtyWrap instanceof HTMLElement) {
      const qtyInput = qtyWrap.querySelector('input[type="number"]');
      if (qtyInput instanceof HTMLInputElement) {
        const parsed = parseInt(qtyInput.value, 10);
        if (!Number.isNaN(parsed) && parsed > 0) {
          quantity = parsed;
        }
      }
    }

    this.#disableCartItems();

    try {
      // Step 1: Remove old line item
      const removeBody = JSON.stringify({ line: parseInt(line), quantity: 0 });
      await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body: removeBody }));

      // Step 2: Add new variant
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const addBody = JSON.stringify({
        items: [{ id: parseInt(variantId), quantity }],
        sections: Array.from(sectionsToUpdate).join(','),
      });

      const addResponse = await fetch(`${Theme.routes.root}cart/add.js`, fetchConfig('json', { body: addBody }));
      const addResult = await addResponse.json();

      if (addResult.status) {
        // Add failed — re-add original variant as recovery
        console.error('Size change failed, recovering:', addResult.description);
        const recoverBody = JSON.stringify({
          items: [{ id: parseInt(currentVariantId), quantity }],
          sections: Array.from(sectionsToUpdate).join(','),
        });
        const recoverResponse = await fetch(`${Theme.routes.root}cart/add.js`, fetchConfig('json', { body: recoverBody }));
        const recoverResult = await recoverResponse.json();
        if (recoverResult.sections?.[this.sectionId]) {
          morphSection(this.sectionId, recoverResult.sections[this.sectionId]);
        }
        return;
      }

      // Step 3: Re-render
      const sectionsHtml = addResult.sections;
      if (sectionsHtml?.[this.sectionId]) {
        morphSection(this.sectionId, sectionsHtml[this.sectionId]);
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }

      // Step 4: Dispatch update event
      const newCartHiddenItemCount = new DOMParser()
        .parseFromString(sectionsHtml?.[this.sectionId] || '', 'text/html')
        .querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: sectionsHtml,
        })
      );
    } catch (error) {
      console.error('Size change error:', error);
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
